import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ChainsPage from "./pages/ChainsPage";
import SectionsPage from "./pages/SectionsPage";
import StoresPage from "./pages/StoresPage";
import StoreConfigPage from "./pages/StoreConfigPage";
import SkusPage from "./pages/SkusPage";
import UsersPage from "./pages/UsersPage";
import AssignmentsPage from "./pages/AssignmentsPage";
import RouteTemplatesPage from "./pages/RouteTemplatesPage";
import VisitsPage from "./pages/VisitsPage";
import VisitDetailPage from "./pages/VisitDetailPage";
import AlertsPage from "./pages/AlertsPage";
import BulkUploadPage from "./pages/BulkUploadPage";
import HealthPage from "./pages/HealthPage";
import AIMonitorPage from "./pages/AIMonitorPage";
import DeviationsPage from "./pages/DeviationsPage";
import ShareOfShelfPage from "./pages/ShareOfShelfPage";
import ExecutiveAgentPage from "./pages/ExecutiveAgentPage";
import RulesEnginePage from "./pages/RulesEnginePage";
import PriorityCenterPage from "./pages/PriorityCenterPage";
import Layout from "./components/Layout";

// Protected Route component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Health check route - public, no auth needed */}
      <Route path="/health" element={<HealthPage />} />

      {/* Public routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to="/" replace /> : <LoginPage />} 
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Admin routes - Catalogs */}
      <Route
        path="/chains"
        element={
          <ProtectedRoute requireAdmin>
            <ChainsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections"
        element={
          <ProtectedRoute requireAdmin>
            <SectionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stores"
        element={
          <ProtectedRoute requireAdmin>
            <StoresPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stores/:storeId/config"
        element={
          <ProtectedRoute requireAdmin>
            <StoreConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/skus"
        element={
          <ProtectedRoute requireAdmin>
            <SkusPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requireAdmin>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assignments"
        element={
          <ProtectedRoute requireAdmin>
            <AssignmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/route-templates"
        element={
          <ProtectedRoute requireAdmin>
            <RouteTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bulk-upload"
        element={
          <ProtectedRoute requireAdmin>
            <BulkUploadPage />
          </ProtectedRoute>
        }
      />

      {/* Supervisor routes (accessible by both admin and supervisor) */}
      <Route
        path="/visits"
        element={
          <ProtectedRoute>
            <VisitsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits/:id"
        element={
          <ProtectedRoute>
            <VisitDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-monitor"
        element={
          <ProtectedRoute>
            <AIMonitorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deviations"
        element={
          <ProtectedRoute>
            <DeviationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/share-of-shelf"
        element={
          <ProtectedRoute>
            <ShareOfShelfPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive-agent"
        element={
          <ProtectedRoute>
            <ExecutiveAgentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules-engine"
        element={
          <ProtectedRoute>
            <RulesEnginePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/priority-center"
        element={
          <ProtectedRoute>
            <PriorityCenterPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
